/**
 * T5.17 - Live findings stream (2026-05-24).
 *
 * Wires the existing /v1/activity/findings/stream SSE endpoint (5s poll)
 * into a SOC dashboard page. Unified UEBA + Sonar + Early Warning
 * findings flow in real-time as the scheduled-analyzers (T5.15) persist
 * them. New rows animate in via AnimatePresence prepend.
 *
 * Per CLAUDE.md §13 (DSPM ↔ DAM ↔ DDR independence): this page lives
 * under /activity (DAM module) - the existing /discovery/findings page
 * is for DSPM findings only.
 */
import { motion, AnimatePresence } from 'motion/react';
import { Pause, Play, AlertTriangle, Activity as ActivityIcon } from 'lucide-react';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { useSseStream } from '@/lib/useSseStream';

/**
 * Wire shape from /v1/activity/findings/stream - see
 * apps/api-controller/src/modules/activity/streams.ts streamFindings.
 */
interface FindingDto {
  kind: 'ueba' | 'sonar' | 'early_warning';
  id: string;
  detectedAt: string;
  // UEBA-specific
  metric?: string;
  zScore?: number;
  actorUserId?: string;
  // Sonar-specific
  application?: string;
  klDivergence?: number;
  appliedKlThreshold?: number;
  // Early warning-specific
  indicatorKind?: string;
  // Common
  severity?: 'low' | 'medium' | 'high' | 'critical';
  explanation?: string;
}

function kindBadgeVariant(
  kind: FindingDto['kind'],
): 'critical' | 'warning' | 'info' {
  switch (kind) {
    case 'early_warning':
      return 'critical';
    case 'sonar':
      return 'warning';
    case 'ueba':
      return 'info';
  }
}

function summaryLine(f: FindingDto): string {
  switch (f.kind) {
    case 'ueba':
      return `${f.actorUserId ?? 'actor'}: ${f.metric ?? 'metric'} z=${f.zScore?.toFixed(2) ?? '?'}`;
    case 'sonar':
      return `${f.application ?? 'app'}: KL=${f.klDivergence?.toFixed(3) ?? '?'} (threshold ${f.appliedKlThreshold?.toFixed(3) ?? '?'})`;
    case 'early_warning':
      return `${f.indicatorKind ?? 'reconnaissance'} indicator fired`;
  }
}

export function ActivityFindings() {
  const {
    events: sseEvents,
    paused,
    setPaused,
    connected,
  } = useSseStream<FindingDto>({
    url: '/api/v1/activity/findings/stream',
    bufferSize: 100,
    acceptEventName: (name) => name.startsWith('finding.'),
  });

  return (
    <PageContainer>
      <PageHeader
        title="Live Findings Stream"
        subtitle="UEBA + Sonar + Early Warning findings as the scheduled-analyzers persist them. New rows surface in real time - operators acknowledge or escalate directly from here."
        badges={[
          { label: 'UEBA · Sonar · EW', variant: 'glass' },
          {
            label: paused ? 'Paused' : connected ? 'Live' : 'Connecting…',
            variant: paused ? 'glass' : connected ? 'success' : 'glass',
          },
          { label: `${sseEvents.length} buffered`, variant: 'glass' },
        ]}
        actions={
          <Button
            variant={paused ? 'default' : 'glass'}
            size="sm"
            onClick={() => setPaused(!paused)}
          >
            {paused ? (
              <>
                <Play className="h-4 w-4" /> Resume
              </>
            ) : (
              <>
                <Pause className="h-4 w-4" /> Pause
              </>
            )}
          </Button>
        }
      />

      <GlassCard className="p-0 overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40 font-medium">
          <div className="col-span-2">Detected</div>
          <div className="col-span-1">Kind</div>
          <div className="col-span-1">Severity</div>
          <div className="col-span-7">Summary</div>
          <div className="col-span-1 text-right">ID</div>
        </div>

        {sseEvents.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <ActivityIcon className="h-12 w-12 opacity-30 mb-3 animate-pulse" />
            <p className="text-sm">
              {connected
                ? 'Connected - waiting for findings…'
                : paused
                  ? 'Stream paused.'
                  : 'Connecting…'}
            </p>
            <p className="text-[11px] mt-1 opacity-60">
              Schedulers (ENABLE_SCHEDULED_ANALYZERS) run every 5 min on prod.
            </p>
          </div>
        )}

        <div className="max-h-[680px] overflow-y-auto">
          <AnimatePresence initial={false}>
            {sseEvents.map(({ id, data: f }) => (
              <motion.div
                key={id}
                layout
                initial={{ opacity: 0, x: -20, height: 0 }}
                animate={{ opacity: 1, x: 0, height: 'auto' }}
                exit={{ opacity: 0, x: 20, height: 0 }}
                transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
                className="grid grid-cols-12 px-4 py-3 text-xs items-center border-b border-border/20 hover:bg-muted/30 transition-colors"
                style={
                  f.severity === 'critical'
                    ? { background: 'hsl(var(--sev-critical) / 0.05)' }
                    : undefined
                }
              >
                <div className="col-span-2 font-mono text-[11px] text-muted-foreground">
                  {new Date(f.detectedAt).toLocaleString('en-IN', {
                    hour12: false,
                  })}
                </div>
                <div className="col-span-1">
                  <Badge variant={kindBadgeVariant(f.kind)} className="text-[9px]">
                    {f.kind === 'early_warning' ? 'EW' : f.kind.toUpperCase()}
                  </Badge>
                </div>
                <div className="col-span-1">
                  {f.severity ? (
                    <Badge
                      variant={
                        f.severity === 'critical'
                          ? 'critical'
                          : f.severity === 'high'
                            ? 'warning'
                            : f.severity === 'medium'
                              ? 'info'
                              : 'glass'
                      }
                      className="text-[9px]"
                    >
                      {f.severity}
                    </Badge>
                  ) : (
                    <span className="text-[10px] text-muted-foreground italic">-</span>
                  )}
                </div>
                <div className="col-span-7 min-w-0">
                  <div className="font-mono text-[11px] truncate">{summaryLine(f)}</div>
                  {f.explanation && (
                    <div className="text-[10px] text-muted-foreground truncate opacity-70 mt-0.5">
                      {f.explanation}
                    </div>
                  )}
                </div>
                <div className="col-span-1 text-right">
                  <div className="font-mono text-[10px] text-muted-foreground truncate">
                    {f.id.slice(0, 8)}…
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {sseEvents.length > 0 && (
          <div className="px-4 py-2 border-t border-border/20 text-[10px] text-muted-foreground flex items-center gap-2">
            <AlertTriangle className="h-3 w-3" />
            Findings show the last {sseEvents.length} events; the ack workflow lives on the per-kind drill-down pages (UEBA / Sonar / Early Warning).
          </div>
        )}
      </GlassCard>
    </PageContainer>
  );
}
